import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import {
  Eye, Image, Mail, MailOpen, Star, ShoppingBag, TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-neutral-900 border border-white/10 rounded-sm p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-white/50 text-xs uppercase tracking-wider">{label}</span>
      <Icon className={`h-4 w-4 ${accent || 'text-white/30'}`} />
    </div>
    <p className="text-3xl font-semibold text-white">{value}</p>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-neutral-800 border border-white/20 rounded-sm px-3 py-2 text-xs">
      <p className="text-white/60 mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

const Dashboard = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [popular, setPopular] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API}/analytics/overview`, { headers }),
      axios.get(`${API}/analytics/popular`, { headers }),
      axios.get(`${API}/analytics/trends`, { headers }),
    ])
      .then(([overviewRes, popularRes, trendsRes]) => {
        setOverview(overviewRes.data);
        setPopular(popularRes.data);
        // Format dates for chart display
        setTrends(
          trendsRes.data.map((d) => ({
            ...d,
            label: d.date.slice(5), // "MM-DD"
          }))
        );
      })
      .catch((err) => console.error('Analytics fetch error:', err))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-section">
      <div className="mb-8">
        <h2 className="font-heading text-2xl font-semibold text-white">Dashboard</h2>
        <p className="text-white/50 text-sm mt-1">
          Portfolio performance at a glance
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Image} label="Total Artworks" value={overview?.total_artworks ?? 0} accent="text-teal-400" />
        <StatCard icon={Eye} label="Total Views" value={overview?.total_views ?? 0} accent="text-blue-400" />
        <StatCard icon={Mail} label="Messages" value={overview?.total_messages ?? 0} accent="text-amber-400" />
        <StatCard icon={MailOpen} label="Unread" value={overview?.unread_messages ?? 0} accent="text-red-400" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatCard icon={Star} label="Featured" value={overview?.featured_count ?? 0} accent="text-yellow-400" />
        <StatCard icon={ShoppingBag} label="Available" value={overview?.available_count ?? 0} accent="text-emerald-400" />
        <StatCard icon={TrendingUp} label="Sold" value={overview?.sold_count ?? 0} accent="text-purple-400" />
      </div>

      {/* Trends chart */}
      <div className="bg-neutral-900 border border-white/10 rounded-sm p-6 mb-8" data-testid="trends-chart">
        <h3 className="font-medium text-white mb-1">30-Day Activity</h3>
        <p className="text-white/40 text-xs mb-6">Daily artwork views and contact messages</p>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={trends}>
            <defs>
              <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0d9488" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="msgsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey="label" tick={{ fill: '#666', fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: '#666', fontSize: 10 }} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="views" name="Views" stroke="#0d9488" fill="url(#viewsGrad)" strokeWidth={2} />
            <Area type="monotone" dataKey="messages" name="Messages" stroke="#f59e0b" fill="url(#msgsGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Popular artworks */}
      <div className="bg-neutral-900 border border-white/10 rounded-sm p-6" data-testid="popular-artworks">
        <h3 className="font-medium text-white mb-1">Most Viewed Artworks</h3>
        <p className="text-white/40 text-xs mb-6">Top pieces by total views</p>

        {popular.length === 0 ? (
          <p className="text-white/40 text-sm py-8 text-center">
            No views recorded yet. Views are tracked when visitors click on artworks in the portfolio.
          </p>
        ) : (
          <div className="space-y-3">
            {popular.map((art, i) => {
              const maxViews = popular[0]?.view_count || 1;
              const pct = Math.round((art.view_count / maxViews) * 100);
              return (
                <div key={art.id} className="flex items-center gap-4" data-testid={`popular-art-${i}`}>
                  <span className="text-white/30 text-xs w-5 text-right">{i + 1}</span>
                  <div className="w-10 h-10 bg-neutral-800 rounded-sm overflow-hidden flex-shrink-0">
                    {art.image_url && (
                      <img src={art.image_url} alt={art.title} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{art.title}</p>
                    <div className="mt-1 h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-500 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-white/60 text-sm font-medium tabular-nums">
                    {art.view_count}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
